import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  Button,
  Chip,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";
import { SaveButton, CloseButton, AddButton } from "../../../common/Buttons";

const theme = createTheme({
  palette: {
    primary: {
      main: "#636096",
    },
    secondary: {
      main: "#00bcd4",
    },
    background: {
      default: "#f5f5f5",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          textTransform: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
        },
      },
    },
  },
});

const ModalContent = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 600px;
  border-radius: 4px;
  background: #f5f5f5;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 32px;
`;

const ScrollContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  max-width: 65%;

  &::-webkit-scrollbar {
    height: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(99, 96, 150, 0.7);
    border-radius: 5px;
  }
`;

const ButtonContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MotionCard = motion(Card);

function CreateMchUsableProc({
  table,
  machineMasterData,
  processMasterData,
  clickedProcId,
  open,
  onClose,
  clickedItemId,
  itemData,
  tableData,
  handleCreateMchUsableProc,
}) {
  const [selectedCards, setSelectedCards] = useState(
    tableData?.map((item) => item.machine_id) || []
  );
  const handleCardClick = (index) => {
    setSelectedCards((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };
  const currentProc = processMasterData.filter(
    (c) => c.proc_id === clickedProcId && c.item_id === clickedItemId
  )[0];
  const currentItem = itemData.filter((c) => c.item_id === clickedItemId)[0];
  const handleSubmit = () => {
    handleCreateMchUsableProc({ values: selectedCards, table });
  };
  return (
    <ThemeProvider theme={theme}>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        disableScrollLock={true}
        disableEnforceFocus
        disableAutoFocus
        keepMounted
      >
        <ModalContent>
          <Typography
            variant="h4"
            component="h2"
            fontWeight="bold"
            color="primary.main"
            mb={2}
            sx={{ textAlign: "center" }}
          >
            장비 선택
          </Typography>
          <Typography variant="subtitle1" mb={3} sx={{ textAlign: "center" }}>
            제품&nbsp;:&nbsp;{currentItem.item_name}
            &nbsp;/&nbsp;공정&nbsp;:&nbsp;{currentProc.proc_name}
          </Typography>
          <Grid container spacing={1} sx={{ mb: 3 }}>
            <AnimatePresence>
              {machineMasterData.map((machine, idx) => (
                <Grid item xs={2} key={idx}>
                  <MotionCard
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <CardActionArea
                      onClick={() => handleCardClick(machine.machine_id)}
                      sx={{
                        height: 50,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        bgcolor: selectedCards.includes(machine.machine_id)
                          ? "primary.main"
                          : "inherit",
                        color: selectedCards.includes(machine.machine_id)
                          ? "primary.contrastText"
                          : "inherit",
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontSize: "0.78rem" }}>
                        {machine.machine_name}
                      </Typography>
                    </CardActionArea>
                  </MotionCard>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <ButtonContainer>
              <ScrollContainer>
                <AnimatePresence>
                  {selectedCards.map((selectedId) => {
                    const machine = machineMasterData.find(
                      (m) => m.machine_id === selectedId
                    );
                    return (
                      <motion.div
                        key={selectedId}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Chip
                          label={machine?.machine_name}
                          onDelete={() => handleCardClick(selectedId)}
                          color="primary"
                          size="small"
                          sx={{ mr: 0.5 }}
                        />
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </ScrollContainer>
              <Box>
                <AddButton onClick={handleSubmit} sx={{ mr: 1 }} />
                <CloseButton onClick={onClose} variant="outlined" />
              </Box>
            </ButtonContainer>
          </Box>
        </ModalContent>
      </Modal>
    </ThemeProvider>
  );
}

export default CreateMchUsableProc;
